import { API_URL } from '@/config';
import { formatDate } from '@/utils/dateFormat';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Vote, Trophy, User, Calendar, ArrowLeft, Loader2, CheckCircle, Share2, Mail, Phone, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import axios from 'axios';
import { toast } from 'sonner';
import { getImageUrl } from '@/utils/imageUrl';

const API = `${API_URL}/api`;

// Default design configuration
const defaultDesign = {
  header_title: 'Vote for Your Favorite Nominee',
  header_subtitle: 'Your vote matters! Support the nominee by casting your vote.',
  show_event_banner: true,
  primary_color: '#D4AF37',
  secondary_color: '#0F172A',
  background_gradient_start: '#0F172A',
  background_gradient_end: '#1E293B',
  card_bg_color: '#FFFFFF',
  text_color: '#0F172A',
  show_nominee_photo: true,
  nominee_photo_size: 'large',
  show_category: true,
  show_vote_count: true,
  show_description: true,
  vote_button_text: 'Vote Now',
  vote_button_color: '#D4AF37',
  footer_text: 'Thank you for participating!',
  show_share_button: true,
  logo_url: '',
  banner_url: '',
  voter_fields: [
    { id: 'name', label: 'Full Name', type: 'text', required: true, enabled: true, icon: 'user' },
    { id: 'email', label: 'Email Address', type: 'email', required: true, enabled: true, icon: 'mail' },
    { id: 'phone', label: 'Phone Number', type: 'tel', required: false, enabled: true, icon: 'phone' },
  ],
  enable_otp_verification: false,
  otp_field: 'both',
  otp_message: 'Your OTP for voting is: {OTP}. Valid for 5 minutes.',
};

const fieldIcons = {
  user: User,
  mail: Mail,
  phone: Phone
};

const photoSizeClasses = {
  small: 'h-48',
  medium: 'h-64',
  large: 'h-80'
};

const PublicVotingPage = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [nominee, setNominee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVoteDialog, setShowVoteDialog] = useState(false);
  const [voting, setVoting] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);
  const [design, setDesign] = useState(defaultDesign);
  const [voterData, setVoterData] = useState({});
  
  // OTP states
  const [otpStep, setOtpStep] = useState('form'); // 'form', 'otp', 'success'
  const [otp, setOtp] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  useEffect(() => {
    loadNomineeData();
    loadDesignSettings();
  }, [submissionId]);

  const loadNomineeData = async () => {
    try {
      const response = await axios.get(`${API}/votes/nominee/${submissionId}`);
      setNominee(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nominee not found');
    } finally {
      setLoading(false);
    }
  };

  const loadDesignSettings = async () => {
    try {
      // Try to load design settings (public endpoint or cached)
      const response = await axios.get(`${API}/settings/vote-link-design/public`);
      if (response.data) {
        setDesign(prev => ({ ...prev, ...response.data }));
        // Initialize voter data based on design fields
        const initialVoterData = {};
        response.data.voter_fields?.forEach(field => {
          if (field.enabled) {
            initialVoterData[field.id] = '';
          }
        });
        setVoterData(initialVoterData);
      }
    } catch (error) {
      // Use default design if settings not available
      console.log('Using default design settings');
      const initialVoterData = {};
      defaultDesign.voter_fields.forEach(field => {
        if (field.enabled) {
          initialVoterData[field.id] = '';
        }
      });
      setVoterData(initialVoterData);
    }
  };

  const handleSendOtp = async () => {
    // Validate required fields first
    const enabledFields = design.voter_fields.filter(f => f.enabled);
    for (const field of enabledFields) {
      if (field.required && !voterData[field.id]) {
        toast.error(`${field.label} is required`);
        return;
      }
    }

    // Check if we have phone or email for OTP
    const phone = voterData.phone;
    const email = voterData.email;
    
    if (design.otp_field === 'both' && !phone && !email) {
      toast.error('Please provide either phone or email for OTP verification');
      return;
    }
    if (design.otp_field === 'phone' && !phone) {
      toast.error('Phone number is required for OTP verification');
      return;
    }
    if (design.otp_field === 'email' && !email) {
      toast.error('Email is required for OTP verification');
      return;
    }

    setSendingOtp(true);
    try {
      const response = await axios.post(`${API}/votes/send-otp`, {
        phone: phone || null,
        email: email || null,
        submission_id: submissionId
      });
      toast.success(response.data.message);
      setOtpStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtpAndVote = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setVerifyingOtp(true);
    try {
      // Verify OTP
      await axios.post(`${API}/votes/verify-otp`, {
        phone: voterData.phone || null,
        email: voterData.email || null,
        submission_id: submissionId,
        otp
      });

      // Cast vote
      await castVote();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'OTP verification failed');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const castVote = async () => {
    try {
      await axios.post(`${API}/votes/cast`, {
        submission_id: submissionId,
        voter_email: voterData.email || `anonymous_${Date.now()}@vote.com`,
        voter_name: voterData.name || 'Anonymous',
        voter_phone: voterData.phone || ''
      });
      
      setHasVoted(true);
      setShowVoteDialog(false);
      setOtpStep('form');
      setOtp('');
      toast.success('Vote cast successfully!');
      
      // Refresh nominee data to get updated vote count
      loadNomineeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cast vote');
    }
  };

  const handleVote = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const enabledFields = design.voter_fields.filter(f => f.enabled);
    for (const field of enabledFields) {
      if (field.required && !voterData[field.id]) {
        toast.error(`${field.label} is required`);
        return;
      }
    }

    if (design.enable_otp_verification) {
      // Start OTP flow
      await handleSendOtp();
    } else {
      // Direct vote without OTP
      setVoting(true);
      await castVote();
      setVoting(false);
    }
  };

  const shareVotingLink = async () => {
    const url = window.location.href;
    const text = `Vote for ${nominee?.nominee_name} in ${nominee?.event_title}!`;
    
    if (navigator.share) {
      try {
        await navigator.share({ title: text, url });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Voting link copied to clipboard!');
      } catch (err) {
        toast.error('Failed to copy link');
      }
    }
  };

  const updateVoterField = (fieldId, value) => {
    setVoterData(prev => ({ ...prev, [fieldId]: value }));
  };

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center" 
        style={{ background: `linear-gradient(135deg, ${design.background_gradient_start} 0%, ${design.background_gradient_end} 100%)` }}
      >
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: design.primary_color }} />
          <p className="text-white">Loading nominee details...</p>
        </div>
      </div>
    );
  }

  if (!nominee) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center" 
        style={{ background: `linear-gradient(135deg, ${design.background_gradient_start} 0%, ${design.background_gradient_end} 100%)` }}
      >
        <Card className="max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-bold mb-2">Nominee Not Found</h2>
            <p className="text-muted-foreground mb-4">This voting link may be invalid or the voting period has ended.</p>
            <Button onClick={() => navigate('/')}>Go to Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen" 
      style={{ background: `linear-gradient(135deg, ${design.background_gradient_start} 0%, ${design.background_gradient_end} 100%)` }}
    >
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-sm border-b border-white/20">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white hover:text-yellow-400 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Home</span>
          </button>
          <div className="flex items-center gap-2">
            {design.logo_url ? (
              <img src={getImageUrl(design.logo_url)} alt="Logo" className="h-8 object-contain" />
            ) : (
              <>
                <img src="/lions-favicon.png" alt="Lions Club" className="w-8 h-8" />
                <span className="text-white font-semibold hidden sm:inline">Lions Club Jaipur Hawamahal</span>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Custom Banner */}
        {design.show_event_banner && (design.banner_url || nominee.event_banner) && (
          <div className="mb-6 rounded-xl overflow-hidden">
            <img 
              src={getImageUrl(design.banner_url || nominee.event_banner)} 
              alt={nominee.event_title}
              className="w-full h-32 object-cover"
              onError={(e) => { e.target.parentElement.style.display = 'none'; }}
            />
          </div>
        )}

        {/* Event Title */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">{design.header_title || nominee.event_title}</h1>
          <p className="text-white/60">{design.header_subtitle || 'Cast your vote for'}</p>
        </div>

        {/* Nominee Card */}
        <Card className="mb-6 overflow-hidden" style={{ backgroundColor: design.card_bg_color }}>
          {/* Nominee Photo */}
          {design.show_nominee_photo && (
            <div className={`relative ${photoSizeClasses[design.nominee_photo_size]} bg-gradient-to-b from-gray-100 to-gray-200`}>
              {nominee.nominee_photo ? (
                <img 
                  src={getImageUrl(nominee.nominee_photo)} 
                  alt={nominee.nominee_name}
                  className="w-full h-full object-cover"
                  onError={(e) => { 
                    e.target.style.display = 'none'; 
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={`absolute inset-0 flex items-center justify-center ${nominee.nominee_photo ? 'hidden' : ''}`}>
                <User className="w-24 h-24 text-gray-400" />
              </div>
              
              {/* Category Badge */}
              {design.show_category && nominee.category && (
                <div className="absolute top-4 right-4">
                  <span className="px-4 py-2 rounded-full text-sm font-medium text-white" 
                        style={{ backgroundColor: design.primary_color }}>
                    {nominee.category}
                  </span>
                </div>
              )}

              {/* Vote Count Badge */}
              {design.show_vote_count && (
                <div className="absolute bottom-4 left-4 bg-black/70 backdrop-blur-sm rounded-lg px-4 py-2">
                  <div className="flex items-center gap-2 text-white">
                    <Trophy className="w-5 h-5" style={{ color: design.primary_color }} />
                    <span className="text-xl font-bold">{nominee.vote_count}</span>
                    <span className="text-sm opacity-80">votes</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <CardContent className="p-6">
            {/* Nominee Name */}
            <h2 className="text-2xl font-bold text-center mb-4" style={{ color: design.text_color }}>
              {nominee.nominee_name}
            </h2>

            {/* Description */}
            {design.show_description && nominee.description && (
              <p className="text-center text-muted-foreground mb-6">
                {nominee.description}
              </p>
            )}

            {/* Voting End Date */}
            {nominee.voting_end_date && (
              <div className="text-center text-sm text-muted-foreground mb-6">
                <Calendar className="w-4 h-4 inline mr-1" />
                Voting ends: {formatDate(nominee.voting_end_date)}
              </div>
            )}

            {/* Vote Button */}
            {hasVoted ? (
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-green-100 text-green-700">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Thank you for voting!</span>
                </div>
              </div>
            ) : (
              <Button
                onClick={() => setShowVoteDialog(true)}
                className="w-full text-lg py-6"
                style={{ backgroundColor: design.vote_button_color, color: design.secondary_color }}
                data-testid="vote-btn"
              >
                <Vote className="w-5 h-5 mr-2" />
                {design.enable_otp_verification 
                  ? `${design.vote_button_text} (OTP Required)` 
                  : design.vote_button_text}
              </Button>
            )}

            {/* Share Button */}
            {design.show_share_button && (
              <Button
                variant="outline"
                className="w-full mt-3"
                onClick={shareVotingLink}
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share Voting Link
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Footer Text */}
        <p className="text-center text-white/40 text-sm">
          {design.footer_text || 'One vote per email address. You can vote for multiple nominees.'}
        </p>
      </main>

      {/* Footer */}
      <footer className="bg-black/20 py-4 mt-8">
        <div className="max-w-2xl mx-auto px-4 text-center text-white/60 text-sm">
          © 2026 All rights reserved with Cupid Compusoft Pvt. Ltd.
        </div>
      </footer>

      {/* Vote Dialog */}
      <Dialog open={showVoteDialog} onOpenChange={(open) => {
        setShowVoteDialog(open);
        if (!open) {
          setOtpStep('form');
          setOtp('');
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Vote className="w-5 h-5" style={{ color: design.primary_color }} />
              {otpStep === 'otp' ? 'Verify OTP' : 'Cast Your Vote'}
            </DialogTitle>
          </DialogHeader>
          
          {otpStep === 'form' ? (
            <form onSubmit={handleVote}>
              <div className="space-y-4 py-4">
                <p className="text-sm text-muted-foreground">
                  You are voting for <strong>{nominee.nominee_name}</strong>
                </p>
                
                {/* Dynamic Voter Fields */}
                {design.voter_fields.filter(f => f.enabled).map((field) => {
                  const IconComponent = fieldIcons[field.icon] || User;
                  return (
                    <div key={field.id}>
                      <Label>{field.label} {field.required && '*'}</Label>
                      <div className="relative">
                        <IconComponent className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type={field.type}
                          value={voterData[field.id] || ''}
                          onChange={(e) => updateVoterField(field.id, e.target.value)}
                          placeholder={field.label}
                          className="pl-10"
                          required={field.required}
                          data-testid={`voter-${field.id}-input`}
                        />
                      </div>
                    </div>
                  );
                })}
                
                {/* OTP Info */}
                {design.enable_otp_verification && (
                  <Alert className="border-green-200 bg-green-50">
                    <Shield className="w-4 h-4 text-green-600" />
                    <AlertDescription className="text-green-800 text-sm">
                      OTP verification is required. A code will be sent to your {
                        design.otp_field === 'both' ? 'phone and/or email' : design.otp_field
                      }.
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowVoteDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={voting || sendingOtp}
                  style={{ backgroundColor: design.vote_button_color, color: design.secondary_color }}
                  data-testid="confirm-vote-btn"
                >
                  {(voting || sendingOtp) ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {design.enable_otp_verification ? 'Sending OTP...' : 'Voting...'}
                    </>
                  ) : (
                    <>
                      {design.enable_otp_verification ? <Shield className="w-4 h-4 mr-2" /> : <Vote className="w-4 h-4 mr-2" />}
                      {design.enable_otp_verification ? 'Send OTP & Vote' : 'Confirm Vote'}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground text-center">
                Enter the 6-digit OTP sent to your {design.otp_field === 'both' ? 'phone/email' : design.otp_field}
              </p>
              
              <div>
                <Label>Enter OTP</Label>
                <Input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest font-mono"
                  maxLength={6}
                  data-testid="otp-input"
                />
              </div>

              <div className="flex justify-center">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setOtpStep('form')}
                  className="text-muted-foreground"
                >
                  ← Back to form
                </Button>
              </div>

              <DialogFooter className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={handleSendOtp} 
                  disabled={sendingOtp}
                  className="flex-1"
                >
                  {sendingOtp ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Resend OTP'}
                </Button>
                <Button 
                  onClick={handleVerifyOtpAndVote} 
                  disabled={verifyingOtp || otp.length !== 6}
                  style={{ backgroundColor: design.vote_button_color, color: design.secondary_color }}
                  className="flex-1"
                  data-testid="verify-otp-btn"
                >
                  {verifyingOtp ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Verify & Vote
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicVotingPage;
